import React, { useState, useEffect, useContext } from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import MessageContext from "../../MessageContext";
import classes from "./styles.module.css";

var PROVIDER_CONFIGS = {
    hydracode: {
        label: "Hydracode",
        description: "Шлюз Hydracode",
        processingId: 25,
        dataKey: "processingAuth",
        namePattern: /hydra/i,
        fields: [
            { key: "baseUrl", label: "Адрес API", type: "text", required: true, defaultValue: "https://engine.hydracode.com", placeholder: "https://engine.hydracode.com", hint: "Только домен, без пути (например https://engine.hydracode.com)" },
            { key: "apiKey", label: "API ключ", type: "text", required: true, sensitive: true, placeholder: "API ключ" },
            { key: "signingKey", label: "Ключ подписи", type: "text", required: false, sensitive: true, placeholder: "Ключ подписи" },
        ],
    },
    paycraft: {
        label: "Paycraft",
        description: "Обработка Paycraft",
        processingId: 27,
        dataKey: "paycraft",
        namePattern: /paycraft|craft/i,
        fields: [
            { key: "baseUrl", label: "Адрес API", type: "text", required: true, placeholder: "https://api.example.com", hint: "Только домен, без пути" },
            { key: "h2hPoint", label: "Точка H2H", type: "text", required: true, placeholder: "ID точки H2H" },
            { key: "widgetPoint", label: "Точка виджета", type: "text", required: true, placeholder: "ID точки виджета" },
            { key: "privateKeyH2H", label: "Приватный ключ H2H", type: "textarea", required: true, sensitive: true, placeholder: "-----BEGIN PRIVATE KEY-----" },
            { key: "privateKeyWidget", label: "Приватный ключ виджета", type: "textarea", required: true, sensitive: true, placeholder: "-----BEGIN PRIVATE KEY-----" },
            { key: "h2hEndpoint", label: "Адрес H2H", type: "text", required: false, placeholder: "По умолчанию: не задан" },
            { key: "widgetEndpoint", label: "Адрес виджета", type: "text", required: false, placeholder: "По умолчанию: не задан" },
            { key: "serviceH2H", label: "Сервис H2H", type: "number", required: false, defaultValue: 2, placeholder: "По умолчанию: 2" },
            { key: "serviceWidget", label: "Сервис виджета", type: "number", required: false, defaultValue: 3, placeholder: "По умолчанию: 3" },
            { key: "sourceCode", label: "Код источника", type: "text", required: false, defaultValue: "ELQR", placeholder: "По умолчанию: ELQR" },
        ],
    },
    finstar: {
        label: "FinStar",
        description: "FinStar (4pmts)",
        processingId: 26,
        dataKey: "finstar",
        namePattern: /fin/i,
        fields: [
            { key: "merchantId", label: "ID мерчанта", type: "text", required: true, placeholder: "ID мерчанта" },
            { key: "pubKeyId", label: "ID публичного ключа", type: "text", required: true, placeholder: "ID публичного ключа" },
            { key: "privateKey", label: "Приватный ключ", type: "textarea", required: true, sensitive: true, placeholder: "-----BEGIN PRIVATE KEY-----" },
            { key: "baseUrl", label: "Адрес API", type: "text", required: false, defaultValue: "https://api.4pmts.com", placeholder: "По умолчанию: https://api.4pmts.com" },
            { key: "shopIdCard", label: "ID магазина для карт", type: "number", required: false, defaultValue: 302, placeholder: "По умолчанию: 302" },
            { key: "shopIdSbp", label: "ID магазина для СБП", type: "number", required: false, defaultValue: 303, placeholder: "По умолчанию: 303" },
            { key: "pointId", label: "ID точки", type: "text", required: false, placeholder: "По умолчанию: не задан" },
            { key: "recipientType", label: "Тип получателя", type: "number", required: false, defaultValue: 1, placeholder: "По умолчанию: 1" },
        ],
    },
    tkb: {
        label: "TransCapitalBank",
        description: "Обработка TKB",
        processingId: 8,
        dataKey: "processingAuth",
        namePattern: /tkb|transcapital/i,
        fields: [
            { key: "login", label: "Логин", type: "text", required: true, placeholder: "Логин" },
            { key: "secret", label: "Секрет", type: "text", required: true, sensitive: true, placeholder: "Секрет" },
        ],
    },
    crm: {
        label: "CRM",
        description: "Только CRM выплаты (внутренний API)",
        dataKey: null,
        namePattern: /crm/i,
        fields: [],
    },
};

var PROVIDER_KEYS = ["hydracode", "paycraft", "finstar", "tkb", "crm"];

function maskSensitive(value) {
    if (!value || typeof value !== "string") return "";
    if (value.length <= 8) return "****";
    return value.substring(0, 4) + "..." + value.substring(value.length - 4);
}

/**
 * TerminalAddWizard — 6-шаговый визард создания терминала.
 *
 * Props:
 *   token (string)
 *   onCreated (function) — вызывается после успешного создания
 *   onClose (function) — закрыть визард
 */
function TerminalAddWizard(props) {
    var token = props.token;
    var onCreated = props.onCreated;
    var onClose = props.onClose;

    var _msg = useContext(MessageContext);
    var onMessage = _msg.onMessage;

    // Current step (1-6)
    var _step = useState(1);
    var step = _step[0];
    var setStep = _step[1];

    // Data sources
    var _projects = useState([]);
    var projects = _projects[0];
    var setProjects = _projects[1];

    var _loading = useState(true);
    var loading = _loading[0];
    var setLoading = _loading[1];

    var _creating = useState(false);
    var creating = _creating[0];
    var setCreating = _creating[1];

    // Project search
    var _projectSearch = useState("");
    var projectSearch = _projectSearch[0];
    var setProjectSearch = _projectSearch[1];

    var _projectDropOpen = useState(false);
    var projectDropOpen = _projectDropOpen[0];
    var setProjectDropOpen = _projectDropOpen[1];

    // Form data
    var _form = useState({
        projectUuid: "",
        name: "",
        status: 1,
        providerKey: "",
        providerName: "",
        providerAuth: {},
        isSBP: false,
        h2h: false,
        preAuth: false,
        recurring: false,
        partialRefund: false,
        partialReversal: false,
        crmPayoutEnabled: false,
        crmSbpPayoutEnabled: false,
        unlimcoPayoutEnabled: false,
        paymentMethod: "",
        feePaymentCost: "",
        feePaymentFixed: "",
        feePaymentMin: "",
        feePayoutCost: "",
        feePayoutFixed: "",
        feePayoutMin: "",
        feeRefundCost: "",
        feeRefundFixed: "",
        feeRefundMin: "",
    });
    var form = _form[0];
    var setForm = _form[1];

    // Load projects and providers on mount
    useEffect(function () {
        var api = new Api({ token: token });

        api.getProjectsList({ page: 1, itemsPerPage: 10000, order: "DESC", filters: [] })
        .then(function (result) {
            setProjects((result && result.data) ? result.data : []);
            setLoading(false);
        }).catch(function (err) {
            Sentry.captureException(err);
            setLoading(false);
        });
    }, [token]);

    function onChange(field, value) {
        var next = {};
        for (var k in form) { next[k] = form[k]; }
        next[field] = value;
        setForm(next);
    }

    function onProviderSelect(key) {
        var next = {};
        for (var k in form) { next[k] = form[k]; }
        next.providerKey = key;
        next.providerAuth = {};
        if (key === "crm") {
            next.crmPayoutEnabled = true;
        }
        setForm(next);
    }

    function onAuthChange(fieldKey, value) {
        var next = {};
        for (var k in form) { next[k] = form[k]; }
        var authCopy = {};
        for (var ak in form.providerAuth) { authCopy[ak] = form.providerAuth[ak]; }
        authCopy[fieldKey] = value;
        next.providerAuth = authCopy;
        setForm(next);
    }

    function canGoNext() {
        if (step === 1) {
            return form.projectUuid !== "" && form.name.trim() !== "";
        }
        if (step === 2) {
            return form.providerKey !== "" && form.providerName.trim() !== "";
        }
        if (step === 3) {
            var cfg = PROVIDER_CONFIGS[form.providerKey];
            if (!cfg) return false;
            if (cfg.fields.length === 0) return true;
            for (var i = 0; i < cfg.fields.length; i++) {
                var f = cfg.fields[i];
                if (f.required) {
                    var val = form.providerAuth[f.key];
                    if (val === undefined || val === null || String(val).trim() === "") {
                        return false;
                    }
                }
            }
            return true;
        }
        if (step === 4) {
            if (form.providerKey === "crm") return true;
            return form.paymentMethod !== "";
        }
        // step 5 (fees) — always OK (optional)
        return true;
    }

    function goNext() {
        if (step < 6) setStep(step + 1);
    }

    function goBack() {
        if (step > 1) setStep(step - 1);
    }

    function getProjectName() {
        for (var i = 0; i < projects.length; i++) {
            if (projects[i] && projects[i].uuid === form.projectUuid) {
                return projects[i].name || projects[i].uuid;
            }
        }
        return form.projectUuid || "\u2014";
    }

    function buildAuthData() {
        var cfg = PROVIDER_CONFIGS[form.providerKey];
        if (!cfg) return {};
        var authData = {};
        for (var i = 0; i < cfg.fields.length; i++) {
            var f = cfg.fields[i];
            var val = form.providerAuth[f.key];
            if (val !== undefined && val !== null && String(val).trim() !== "") {
                if (f.type === "number") {
                    authData[f.key] = Number(val);
                } else {
                    authData[f.key] = val;
                }
            } else if (f.defaultValue !== undefined) {
                authData[f.key] = f.defaultValue;
            }
        }
        return authData;
    }

    function create() {
        if (creating) return;
        setCreating(true);

        var api = new Api({ token: token });
        var data = {
            isSBP: form.paymentMethod === "SBP_CROSS_BORDER",
            h2h: !!form.h2h,
            preAuth: !!form.preAuth,
            recurring: !!form.recurring,
            partialRefund: !!form.partialRefund,
            partialReversal: !!form.partialReversal,
            crmPayoutEnabled: !!form.crmPayoutEnabled,
            crmSbpPayoutEnabled: !!form.crmSbpPayoutEnabled,
            unlimcoPayoutEnabled: !!form.unlimcoPayoutEnabled,
            paymentMethod: form.paymentMethod || null,
        };

        // Auto-set payout mode for payout-only providers (TKB, FinStar, CRM)
        var payoutOnlyProviderKeys = ["finstar", "tkb", "crm"];
        if (payoutOnlyProviderKeys.indexOf(form.providerKey) !== -1) {
            data.payoutOnly = true;
            data.crmPayout = { enabled: true };
        }

        // Add fees if any were entered
        var hasFees = form.feePaymentCost || form.feePaymentFixed || form.feePaymentMin ||
            form.feePayoutCost || form.feePayoutFixed || form.feePayoutMin ||
            form.feeRefundCost || form.feeRefundFixed || form.feeRefundMin;
        if (hasFees) {
            data.fee = {
                payment: {
                    cost: form.feePaymentCost ? parseFloat(form.feePaymentCost) : 0,
                    fixedPerTransaction: form.feePaymentFixed ? parseFloat(form.feePaymentFixed) : 0,
                    costFixedMinimum: form.feePaymentMin ? parseFloat(form.feePaymentMin) : 0,
                },
                payout: {
                    cost: form.feePayoutCost ? parseFloat(form.feePayoutCost) : 0,
                    fixedPerTransaction: form.feePayoutFixed ? parseFloat(form.feePayoutFixed) : 0,
                    costFixedMinimum: form.feePayoutMin ? parseFloat(form.feePayoutMin) : 0,
                },
                refund: {
                    cost: form.feeRefundCost ? parseFloat(form.feeRefundCost) : 0,
                    fixedPerTransaction: form.feeRefundFixed ? parseFloat(form.feeRefundFixed) : 0,
                    costFixedMinimum: form.feeRefundMin ? parseFloat(form.feeRefundMin) : 0,
                },
            };
        }

        // Inject provider auth into data
        var cfg = PROVIDER_CONFIGS[form.providerKey];
        if (cfg && cfg.dataKey) {
            data[cfg.dataKey] = buildAuthData();
        }

        var payload = {
            name: form.name,
            status: parseInt(form.status, 10),
            data: data,
            providerName: form.providerName.trim(),
        };

        // Привязка к processing по выбранному провайдеру
        if (cfg && cfg.processingId) {
            payload.processing_id = cfg.processingId;
        }

        api.createTerminal(form.projectUuid, payload).then(function (created) {
            onMessage({
                type: "success",
                message: "\u0422\u0435\u0440\u043C\u0438\u043D\u0430\u043B \u0441\u043E\u0437\u0434\u0430\u043D",
            });
            if (typeof onCreated === "function") onCreated(created);
        }).catch(function (err) {
            Sentry.captureException(err);
            onMessage({
                type: "error",
                message: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0441\u043E\u0437\u0434\u0430\u043D\u0438\u044F \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430",
            });
            setCreating(false);
        });
    }

    // Methods definitions (isSBP убран — управляется через "Способ оплаты")
    var methods = [
        { key: "h2h", label: "Человек к человеку", desc: "Прямое подключение без платёжной формы (host-to-host)" },
        { key: "preAuth", label: "\u041F\u0440\u0435\u0434\u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u044F", desc: "\u0411\u043B\u043E\u043A\u0438\u0440\u043E\u0432\u043A\u0430 \u0441\u0443\u043C\u043C\u044B \u043D\u0430 \u043A\u0430\u0440\u0442\u0435 \u0431\u0435\u0437 \u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044F \u2014 \u0441\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u043F\u043E\u0437\u0436\u0435 \u0432\u0440\u0443\u0447\u043D\u0443\u044E" },
        { key: "recurring", label: "\u0420\u0435\u043A\u0443\u0440\u0440\u0435\u043D\u0442", desc: "\u041F\u043E\u0432\u0442\u043E\u0440\u043D\u044B\u0435 \u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044F \u043F\u043E \u0441\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u043E\u0439 \u043A\u0430\u0440\u0442\u0435 \u0431\u0435\u0437 \u0443\u0447\u0430\u0441\u0442\u0438\u044F \u043A\u043B\u0438\u0435\u043D\u0442\u0430" },
        { key: "partialRefund", label: "\u0427\u0430\u0441\u0442\u0438\u0447\u043D\u044B\u0439 \u0432\u043E\u0437\u0432\u0440\u0430\u0442", desc: "\u0412\u043E\u0437\u0432\u0440\u0430\u0442 \u0447\u0430\u0441\u0442\u0438 \u0441\u0443\u043C\u043C\u044B \u043F\u043E\u0441\u043B\u0435 \u0443\u0441\u043F\u0435\u0448\u043D\u043E\u0433\u043E \u043F\u043B\u0430\u0442\u0435\u0436\u0430" },
        { key: "partialReversal", label: "\u0427\u0430\u0441\u0442\u0438\u0447\u043D\u0430\u044F \u043E\u0442\u043C\u0435\u043D\u0430", desc: "\u041E\u0442\u043C\u0435\u043D\u0430 \u0447\u0430\u0441\u0442\u0438 \u043F\u043B\u0430\u0442\u0435\u0436\u0430 \u0434\u043E \u0435\u0433\u043E \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u044F" },
        { key: "crmPayoutEnabled", label: "CRM выплаты", desc: "Выплаты через CRM-систему (API заявки, реестры)" },
        { key: "crmSbpPayoutEnabled", label: "CRM выплаты СБП", desc: "Приём заявок СБП по телефону и названию банка" },
        { key: "unlimcoPayoutEnabled", label: "\u0412\u044B\u043F\u043B\u0430\u0442\u044B \u0432 Unlimco", desc: "\u041E\u0442\u043F\u0440\u0430\u0432\u043A\u0430 \u0432\u044B\u043F\u043B\u0430\u0442 \u0447\u0435\u0440\u0435\u0437 Unlimco-\u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0443" },
    ];

    var enabledMethods = methods.filter(function (m) { return !!form[m.key]; });

    if (loading) {
        return React.createElement("div", { className: classes.Overlay },
            React.createElement("div", { className: classes.WizardCard },
                React.createElement("div", { className: classes.Loading }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430...")
            )
        );
    }

    // Progress bar
    var stepLabels = ["\u041E\u0441\u043D\u043E\u0432\u043D\u043E\u0435", "\u041F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440", "\u0410\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u044F", "\u041C\u0435\u0442\u043E\u0434\u044B", "\u041A\u043E\u043C\u0438\u0441\u0441\u0438\u0438", "\u041E\u0431\u0437\u043E\u0440"];
    var progressBar = React.createElement("div", { className: classes.ProgressBar },
        [1, 2, 3, 4, 5, 6].map(function (s) {
            var stepClass = classes.ProgressStep;
            if (s === step) stepClass += " " + classes.ProgressStepActive;
            if (s < step) stepClass += " " + classes.ProgressStepDone;
            return React.createElement("div", { key: s, className: stepClass },
                React.createElement("div", { className: classes.ProgressCircle }, s < step ? "\u2713" : s),
                React.createElement("div", { className: classes.ProgressLabel }, stepLabels[s - 1])
            );
        })
    );

    // Step content
    var stepContent = null;

    if (step === 1) {
        stepContent = React.createElement("div", { className: classes.StepContent },
            React.createElement("h3", { className: classes.StepTitle }, "\u0428\u0430\u0433 1: \u041F\u0440\u043E\u0435\u043A\u0442 \u0438 \u043E\u0441\u043D\u043E\u0432\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435"),
            React.createElement("p", { className: classes.StepDescription }, "\u0422\u0435\u0440\u043C\u0438\u043D\u0430\u043B \u043F\u0440\u0438\u0432\u044F\u0437\u044B\u0432\u0430\u0435\u0442\u0441\u044F \u043A \u043F\u0440\u043E\u0435\u043A\u0442\u0443 (\u043C\u0435\u0440\u0447\u0430\u043D\u0442\u0443). \u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043F\u0440\u043E\u0435\u043A\u0442, \u0434\u0430\u0439\u0442\u0435 \u043F\u043E\u043D\u044F\u0442\u043D\u043E\u0435 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0438 \u0443\u043A\u0430\u0436\u0438\u0442\u0435 \u0441\u0442\u0430\u0442\u0443\u0441. \u041D\u0435\u0430\u043A\u0442\u0438\u0432\u043D\u044B\u0439 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B \u043D\u0435 \u0431\u0443\u0434\u0435\u0442 \u043F\u0440\u0438\u043D\u0438\u043C\u0430\u0442\u044C \u043F\u043B\u0430\u0442\u0435\u0436\u0438."),
            React.createElement("div", { className: classes.FormGroup },
                React.createElement("label", { className: classes.Label }, "\u041F\u0440\u043E\u0435\u043A\u0442 *"),
                React.createElement("div", { className: classes.SearchSelect },
                    React.createElement("input", {
                        className: classes.Input,
                        type: "text",
                        value: projectDropOpen ? projectSearch : (function () {
                            for (var i = 0; i < projects.length; i++) {
                                if (projects[i] && projects[i].uuid === form.projectUuid) {
                                    return (projects[i].name || "") + " \u2014 " + projects[i].uuid;
                                }
                            }
                            return "";
                        })(),
                        placeholder: "\u041D\u0430\u0447\u043D\u0438\u0442\u0435 \u043F\u0435\u0447\u0430\u0442\u0430\u0442\u044C \u0434\u043B\u044F \u043F\u043E\u0438\u0441\u043A\u0430...",
                        onFocus: function () {
                            setProjectDropOpen(true);
                            setProjectSearch("");
                        },
                        onChange: function (e) {
                            setProjectSearch(e.target.value);
                            if (!projectDropOpen) setProjectDropOpen(true);
                        },
                        onBlur: function () {
                            setProjectDropOpen(false);
                        },
                    }),
                    form.projectUuid && !projectDropOpen
                        ? React.createElement("span", {
                            className: classes.SearchSelectClear,
                            onClick: function () {
                                onChange("projectUuid", "");
                                setProjectSearch("");
                            },
                        }, "\u2715")
                        : null,
                    projectDropOpen ? React.createElement("div", { className: classes.SearchSelectDropdown },
                        (function () {
                            var query = projectSearch.toLowerCase();
                            var filtered = projects.filter(function (p) {
                                if (!p) return false;
                                var text = ((p.name || "") + " " + (p.uuid || "")).toLowerCase();
                                return !query || text.indexOf(query) !== -1;
                            });
                            if (filtered.length === 0) {
                                return React.createElement("div", { className: classes.SearchSelectEmpty }, "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E");
                            }
                            return filtered.map(function (p) {
                                return React.createElement("div", {
                                    key: p.uuid,
                                    className: classes.SearchSelectOption + (form.projectUuid === p.uuid ? " " + classes.SearchSelectOptionActive : ""),
                                    onMouseDown: function (e) {
                                        e.preventDefault();
                                        onChange("projectUuid", p.uuid);
                                        setProjectDropOpen(false);
                                        setProjectSearch("");
                                    },
                                },
                                    React.createElement("div", { className: classes.SearchSelectName }, p.name || p.uuid),
                                    React.createElement("div", { className: classes.SearchSelectUuid }, p.uuid)
                                );
                            });
                        })()
                    ) : null
                ),
                React.createElement("div", { className: classes.FieldHint }, "\u041C\u0435\u0440\u0447\u0430\u043D\u0442, \u043A \u043A\u043E\u0442\u043E\u0440\u043E\u043C\u0443 \u0431\u0443\u0434\u0435\u0442 \u043F\u0440\u0438\u0432\u044F\u0437\u0430\u043D \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B")
            ),
            React.createElement("div", { className: classes.FormGroup },
                React.createElement("label", { className: classes.Label }, "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430 *"),
                React.createElement("input", {
                    className: classes.Input,
                    type: "text",
                    value: form.name,
                    onChange: function (e) { onChange("name", e.target.value); },
                    placeholder: "\u041D\u0430\u043F\u0440\u0438\u043C\u0435\u0440: Hydra P2P \u043E\u0441\u043D\u043E\u0432\u043D\u043E\u0439",
                }),
                React.createElement("div", { className: classes.FieldHint }, "\u041F\u043E\u043D\u044F\u0442\u043D\u043E\u0435 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0434\u043B\u044F \u0438\u0434\u0435\u043D\u0442\u0438\u0444\u0438\u043A\u0430\u0446\u0438\u0438 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430 \u0432 \u0441\u0438\u0441\u0442\u0435\u043C\u0435")
            ),
            React.createElement("div", { className: classes.FormGroup },
                React.createElement("label", { className: classes.Label }, "\u0421\u0442\u0430\u0442\u0443\u0441"),
                React.createElement("select", {
                    className: classes.Select,
                    value: form.status,
                    onChange: function (e) { onChange("status", parseInt(e.target.value, 10)); },
                },
                    React.createElement("option", { value: 1 }, "\u0410\u043A\u0442\u0438\u0432\u0435\u043D"),
                    React.createElement("option", { value: 0 }, "\u041D\u0435\u0430\u043A\u0442\u0438\u0432\u0435\u043D")
                ),
                React.createElement("div", { className: classes.FieldHint }, "\u041D\u0435\u0430\u043A\u0442\u0438\u0432\u043D\u044B\u0439 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B \u043D\u0435 \u0431\u0443\u0434\u0435\u0442 \u043F\u0440\u0438\u043D\u0438\u043C\u0430\u0442\u044C \u043F\u043B\u0430\u0442\u0435\u0436\u0438")
            )
        );
    }

    if (step === 2) {
        stepContent = React.createElement("div", { className: classes.StepContent },
            React.createElement("h3", { className: classes.StepTitle }, "\u0428\u0430\u0433 2: \u041F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440"),
            React.createElement("p", { className: classes.StepDescription }, "\u041F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440 \u2014 \u044D\u0442\u043E \u043F\u043B\u0430\u0442\u0451\u0436\u043D\u044B\u0439 \u043F\u0440\u043E\u0446\u0435\u0441\u0441\u0438\u043D\u0433, \u0447\u0435\u0440\u0435\u0437 \u043A\u043E\u0442\u043E\u0440\u044B\u0439 \u0431\u0443\u0434\u0443\u0442 \u043F\u0440\u043E\u0445\u043E\u0434\u0438\u0442\u044C \u043F\u043B\u0430\u0442\u0435\u0436\u0438 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430. \u041A\u0430\u0436\u0434\u044B\u0439 \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440 \u0438\u043C\u0435\u0435\u0442 \u0441\u0432\u043E\u0438 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u0438, \u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u0432\u044B \u0437\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u043D\u0430 \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u043C \u0448\u0430\u0433\u0435. \u0412\u044B\u0431\u043E\u0440 \u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u0435\u043D."),
            React.createElement("div", { className: classes.ProviderGrid },
                PROVIDER_KEYS.map(function (key) {
                    var cfg = PROVIDER_CONFIGS[key];
                    var isSelected = form.providerKey === key;
                    return React.createElement("div", {
                        key: key,
                        className: isSelected ? classes.ProviderCardSelected : classes.ProviderCard,
                        onClick: function () { onProviderSelect(key); },
                    },
                        React.createElement("div", { className: classes.ProviderCardTitle }, cfg.label),
                        React.createElement("div", { className: classes.ProviderCardDesc }, cfg.description)
                    );
                })
            ),
            React.createElement("div", { className: classes.FormGroup, style: { marginTop: 20 } },
                React.createElement("label", { className: classes.Label }, "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440\u0430 *"),
                React.createElement("input", {
                    className: classes.Input,
                    type: "text",
                    value: form.providerName,
                    onChange: function (e) { onChange("providerName", e.target.value); },
                    placeholder: "\u041D\u0430\u043F\u0440\u0438\u043C\u0435\u0440: Hydra P2P \u043E\u0441\u043D\u043E\u0432\u043D\u043E\u0439",
                }),
                React.createElement("div", { className: classes.FieldHint }, "\u0411\u0443\u0434\u0435\u0442 \u0441\u043E\u0437\u0434\u0430\u043D \u043D\u043E\u0432\u044B\u0439 \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440 \u0441 \u044D\u0442\u0438\u043C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435\u043C")
            )
        );
    }

    if (step === 3) {
        var providerCfg = PROVIDER_CONFIGS[form.providerKey];
        var authFields = providerCfg ? providerCfg.fields : [];

        stepContent = React.createElement("div", { className: classes.StepContent },
            React.createElement("h3", { className: classes.StepTitle }, "\u0428\u0430\u0433 3: \u0410\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u044F " + (providerCfg ? providerCfg.label : "")),
            authFields.length === 0
                ? React.createElement("p", { className: classes.StepDescription },
                    "CRM-\u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440 \u043D\u0435 \u0442\u0440\u0435\u0431\u0443\u0435\u0442 \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u043E\u043D\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445. \u0422\u0435\u0440\u043C\u0438\u043D\u0430\u043B \u0431\u0443\u0434\u0435\u0442 \u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C \u0442\u043E\u043B\u044C\u043A\u043E \u0447\u0435\u0440\u0435\u0437 \u0432\u043D\u0443\u0442\u0440\u0435\u043D\u043D\u0438\u0439 CRM API. \u041D\u0430\u0436\u043C\u0438\u0442\u0435 \u00AB\u0414\u0430\u043B\u0435\u0435\u00BB \u0434\u043B\u044F \u043F\u0440\u043E\u0434\u043E\u043B\u0436\u0435\u043D\u0438\u044F."
                )
                : React.createElement("p", { className: classes.StepDescription }, "\u042D\u0442\u0438 \u0434\u0430\u043D\u043D\u044B\u0435 \u043D\u0443\u0436\u043D\u044B \u0434\u043B\u044F \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043A API \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440\u0430. \u041F\u043E\u043B\u044F \u0441\u043E \u0437\u0432\u0451\u0437\u0434\u043E\u0447\u043A\u043E\u0439 (*) \u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u044B. \u041E\u0441\u0442\u0430\u043B\u044C\u043D\u044B\u0435 \u043F\u043E\u043B\u044F \u0438\u043C\u0435\u044E\u0442 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E \u0438 \u043C\u043E\u0433\u0443\u0442 \u0431\u044B\u0442\u044C \u043E\u0441\u0442\u0430\u0432\u043B\u0435\u043D\u044B \u043F\u0443\u0441\u0442\u044B\u043C\u0438."),
            authFields.map(function (f) {
                var val = form.providerAuth[f.key];
                if (val === undefined || val === null) val = "";

                var inputEl;
                if (f.type === "textarea") {
                    inputEl = React.createElement("textarea", {
                        className: classes.Textarea,
                        value: val,
                        onChange: function (e) { onAuthChange(f.key, e.target.value); },
                        placeholder: f.placeholder || "",
                        rows: 4,
                    });
                } else if (f.type === "number") {
                    inputEl = React.createElement("input", {
                        className: classes.Input,
                        type: "number",
                        value: val,
                        onChange: function (e) { onAuthChange(f.key, e.target.value); },
                        placeholder: f.placeholder || "",
                    });
                } else {
                    inputEl = React.createElement("input", {
                        className: classes.Input,
                        type: "text",
                        value: val,
                        onChange: function (e) { onAuthChange(f.key, e.target.value); },
                        placeholder: f.placeholder || "",
                    });
                }

                return React.createElement("div", { key: f.key, className: classes.FormGroup },
                    React.createElement("label", { className: classes.Label },
                        f.label + (f.required ? " *" : "")
                    ),
                    inputEl,
                    f.hint
                        ? React.createElement("div", { className: classes.FieldHint }, f.hint)
                        : f.defaultValue !== undefined && !f.required
                            ? React.createElement("div", { className: classes.FieldHint },
                                "\u041F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E: " + f.defaultValue
                            )
                            : null
                );
            })
        );
    }

    if (step === 4) {
        stepContent = React.createElement("div", { className: classes.StepContent },
            React.createElement("h3", { className: classes.StepTitle }, "\u0428\u0430\u0433 4: \u0421\u043F\u043E\u0441\u043E\u0431 \u043E\u043F\u043B\u0430\u0442\u044B \u0438 \u0434\u043E\u043F\u043E\u043B\u043D\u0438\u0442\u0435\u043B\u044C\u043D\u044B\u0435 \u043E\u043F\u0446\u0438\u0438"),
            React.createElement("p", { className: classes.StepDescription }, "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u0432\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0441\u043F\u043E\u0441\u043E\u0431 \u043E\u043F\u043B\u0430\u0442\u044B (\u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u043E), \u0437\u0430\u0442\u0435\u043C \u0432\u043A\u043B\u044E\u0447\u0438\u0442\u0435 \u0434\u043E\u043F\u043E\u043B\u043D\u0438\u0442\u0435\u043B\u044C\u043D\u044B\u0435 \u043E\u043F\u0446\u0438\u0438 \u0435\u0441\u043B\u0438 \u043D\u0443\u0436\u043D\u043E. \u041E\u043F\u0446\u0438\u0438 \u043C\u043E\u0436\u043D\u043E \u0438\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u043F\u043E\u0437\u0436\u0435 \u0432 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0430\u0445 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430."),

            // === Способ оплаты ===
            React.createElement("div", { className: classes.SectionLabel },
                "\u0421\u043F\u043E\u0441\u043E\u0431 \u043E\u043F\u043B\u0430\u0442\u044B",
                form.providerKey !== "crm" ? React.createElement("span", { className: classes.SectionRequired }, "*") : null
            ),
            React.createElement("p", { className: classes.SectionHint },
                form.providerKey === "crm"
                    ? "\u0414\u043B\u044F CRM-\u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430 \u0441\u043F\u043E\u0441\u043E\u0431 \u043E\u043F\u043B\u0430\u0442\u044B \u043D\u0435\u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u0435\u043D. \u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435, \u0435\u0441\u043B\u0438 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B \u0442\u0430\u043A\u0436\u0435 \u043F\u0440\u0438\u043D\u0438\u043C\u0430\u0435\u0442 \u043F\u043B\u0430\u0442\u0435\u0436\u0438."
                    : "\u041E\u043F\u0440\u0435\u0434\u0435\u043B\u044F\u0435\u0442 \u043A\u0430\u043A\u0438\u043C \u0441\u043F\u043E\u0441\u043E\u0431\u043E\u043C \u043A\u043B\u0438\u0435\u043D\u0442 \u0431\u0443\u0434\u0435\u0442 \u043E\u043F\u043B\u0430\u0447\u0438\u0432\u0430\u0442\u044C: \u043F\u0435\u0440\u0435\u0432\u043E\u0434\u043E\u043C \u043D\u0430 \u043A\u0430\u0440\u0442\u0443 (P2P) \u0438\u043B\u0438 \u0447\u0435\u0440\u0435\u0437 \u0421\u0411\u041F. \u0412\u044B\u0431\u043E\u0440 \u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u0435\u043D."
            ),
            React.createElement("div", { className: classes.ProviderGrid },
                React.createElement("div", {
                    className: form.paymentMethod === "P2P_CROSS_BORDER" ? classes.ProviderCardSelected : classes.ProviderCard,
                    onClick: function () { onChange("paymentMethod", form.paymentMethod === "P2P_CROSS_BORDER" ? "" : "P2P_CROSS_BORDER"); },
                },
                    React.createElement("div", { className: classes.ProviderCardTitle }, "P2P"),
                    React.createElement("div", { className: classes.ProviderCardDesc }, "\u041F\u0435\u0440\u0435\u0432\u043E\u0434 \u043D\u0430 \u043A\u0430\u0440\u0442\u0443 (card-to-card)")
                ),
                React.createElement("div", {
                    className: form.paymentMethod === "SBP_CROSS_BORDER" ? classes.ProviderCardSelected : classes.ProviderCard,
                    onClick: function () { onChange("paymentMethod", form.paymentMethod === "SBP_CROSS_BORDER" ? "" : "SBP_CROSS_BORDER"); },
                },
                    React.createElement("div", { className: classes.ProviderCardTitle }, "\u0421\u0411\u041F"),
                    React.createElement("div", { className: classes.ProviderCardDesc }, "\u0421\u0438\u0441\u0442\u0435\u043C\u0430 \u0431\u044B\u0441\u0442\u0440\u044B\u0445 \u043F\u043B\u0430\u0442\u0435\u0436\u0435\u0439 (\u043F\u043E QR-\u043A\u043E\u0434\u0443)")
                )
            ),

            // === Дополнительные опции ===
            React.createElement("div", { className: classes.SectionLabel, style: { marginTop: 24 } },
                "\u0414\u043E\u043F\u043E\u043B\u043D\u0438\u0442\u0435\u043B\u044C\u043D\u044B\u0435 \u043E\u043F\u0446\u0438\u0438"
            ),
            React.createElement("p", { className: classes.SectionHint }, "\u041D\u0435\u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u044B\u0435 \u0432\u043E\u0437\u043C\u043E\u0436\u043D\u043E\u0441\u0442\u0438 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430. \u0412\u043A\u043B\u044E\u0447\u0438\u0442\u0435 \u0442\u043E\u043B\u044C\u043A\u043E \u0442\u043E, \u0447\u0442\u043E \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u0442 \u0432\u0430\u0448 \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440."),
            methods.map(function (m) {
                return React.createElement("div", { key: m.key, style: { marginBottom: 8 } },
                    React.createElement("label", { className: classes.ToggleLabel },
                        React.createElement("input", {
                            type: "checkbox",
                            checked: !!form[m.key],
                            onChange: function (e) { onChange(m.key, e.target.checked); },
                        }),
                        React.createElement("span", { className: classes.ToggleSwitch }),
                        React.createElement("span", { className: classes.ToggleText }, m.label)
                    ),
                    React.createElement("div", { className: classes.ToggleDesc }, m.desc)
                );
            })
        );
    }

    // ===== STEP 5: Fees =====
    if (step === 5) {
        var payoutOnlyProviders = ["finstar", "tkb", "crm"];
        var isPayoutOnly = payoutOnlyProviders.indexOf(form.providerKey) !== -1;

        function onFeeChange(field, value) {
            if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
            onChange(field, value);
        }

        function renderFeeFields(title, desc, costField, fixedField, minField) {
            return React.createElement("div", { style: { border: "1px solid #e0e0e0", borderRadius: "6px", padding: "15px", marginBottom: "15px" } },
                React.createElement("div", { style: { fontSize: "14px", fontWeight: 600, marginBottom: "6px" } }, title),
                React.createElement("div", { style: { fontSize: "12px", color: "#888", marginBottom: "12px" } }, desc),
                React.createElement("div", { style: { display: "flex", gap: "15px" } },
                    React.createElement("div", { style: { flex: 1 } },
                        React.createElement("div", { style: { fontSize: "12px", color: "#666", marginBottom: "4px" } }, "\u041F\u0440\u043E\u0446\u0435\u043D\u0442 (%)"),
                        React.createElement("input", {
                            className: classes.Input,
                            type: "text",
                            value: form[costField],
                            onChange: function (e) { onFeeChange(costField, e.target.value); },
                            placeholder: "0",
                        })
                    ),
                    React.createElement("div", { style: { flex: 1 } },
                        React.createElement("div", { style: { fontSize: "12px", color: "#666", marginBottom: "4px" } }, "\u0424\u0438\u043A\u0441. (\u0440\u0443\u0431)"),
                        React.createElement("input", {
                            className: classes.Input,
                            type: "text",
                            value: form[fixedField],
                            onChange: function (e) { onFeeChange(fixedField, e.target.value); },
                            placeholder: "0",
                        })
                    ),
                    React.createElement("div", { style: { flex: 1 } },
                        React.createElement("div", { style: { fontSize: "12px", color: "#666", marginBottom: "4px" } }, "\u041C\u0438\u043D. (\u0440\u0443\u0431)"),
                        React.createElement("input", {
                            className: classes.Input,
                            type: "text",
                            value: form[minField],
                            onChange: function (e) { onFeeChange(minField, e.target.value); },
                            placeholder: "0",
                        })
                    )
                )
            );
        }

        stepContent = React.createElement("div", { className: classes.StepContent },
            React.createElement("h3", { className: classes.StepTitle }, "\u0428\u0430\u0433 5: \u041A\u043E\u043C\u0438\u0441\u0441\u0438\u0438"),
            React.createElement("p", { className: classes.StepDescription },
                "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043A\u043E\u043C\u0438\u0441\u0441\u0438\u0438 \u0434\u043B\u044F \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430. \u041C\u043E\u0436\u043D\u043E \u043E\u0441\u0442\u0430\u0432\u0438\u0442\u044C \u043F\u0443\u0441\u0442\u044B\u043C\u0438 \u0438 \u043D\u0430\u0441\u0442\u0440\u043E\u0438\u0442\u044C \u043F\u043E\u0437\u0436\u0435."
            ),

            !isPayoutOnly && renderFeeFields(
                "\u041E\u043F\u043B\u0430\u0442\u0430 (Payment)",
                "Purchase, Recurrent, SBP, P2P, QR",
                "feePaymentCost", "feePaymentFixed", "feePaymentMin"
            ),

            renderFeeFields(
                "\u0412\u044B\u043F\u043B\u0430\u0442\u044B (Payout)",
                "Payout, Mobile Payout",
                "feePayoutCost", "feePayoutFixed", "feePayoutMin"
            ),

            !isPayoutOnly && renderFeeFields(
                "\u0412\u043E\u0437\u0432\u0440\u0430\u0442\u044B (Refund)",
                "Refund",
                "feeRefundCost", "feeRefundFixed", "feeRefundMin"
            ),

            React.createElement("div", { style: { fontSize: "12px", color: "#888", marginTop: "10px", padding: "8px", background: "#f5f5f5", borderRadius: "4px" } },
                "\u0424\u043E\u0440\u043C\u0443\u043B\u0430: \u0424\u0438\u043A\u0441. + max(\u0421\u0443\u043C\u043C\u0430 \u00D7 \u041F\u0440\u043E\u0446\u0435\u043D\u0442%, \u041C\u0438\u043D.). \u0412\u0441\u0435 \u0441\u0443\u043C\u043C\u044B \u0432 \u0440\u0443\u0431\u043B\u044F\u0445."
            ),

            isPayoutOnly && React.createElement("div", { style: { fontSize: "12px", color: "#e65100", marginTop: "8px", padding: "8px", background: "#fff3e0", borderRadius: "4px" } },
                "\u042D\u0442\u043E\u0442 \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440 \u0440\u0430\u0431\u043E\u0442\u0430\u0435\u0442 \u0442\u043E\u043B\u044C\u043A\u043E \u043D\u0430 \u0432\u044B\u043F\u043B\u0430\u0442\u044B. \u041A\u043E\u043C\u0438\u0441\u0441\u0438\u0438 Payment \u0438 Refund \u043D\u0435 \u043F\u0440\u0438\u043C\u0435\u043D\u044F\u044E\u0442\u0441\u044F."
            )
        );
    }

    // ===== STEP 6: Review =====
    if (step === 6) {
        var reviewProviderCfg = PROVIDER_CONFIGS[form.providerKey];
        var reviewAuthData = buildAuthData();

        var authReviewRows = [];
        if (reviewProviderCfg) {
            for (var ri = 0; ri < reviewProviderCfg.fields.length; ri++) {
                var rf = reviewProviderCfg.fields[ri];
                var rv = reviewAuthData[rf.key];
                if (rv !== undefined && rv !== null && String(rv) !== "") {
                    var displayVal = rf.sensitive ? maskSensitive(String(rv)) : String(rv);
                    authReviewRows.push(
                        React.createElement("tr", { key: "auth-" + rf.key },
                            React.createElement("td", { className: classes.ReviewLabel }, rf.label),
                            React.createElement("td", null,
                                React.createElement("span", { className: classes.AuthValue }, displayVal)
                            )
                        )
                    );
                }
            }
        }

        stepContent = React.createElement("div", { className: classes.StepContent },
            React.createElement("h3", { className: classes.StepTitle }, "\u0428\u0430\u0433 6: \u041E\u0431\u0437\u043E\u0440"),
            React.createElement("p", { className: classes.StepDescription }, "\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0432\u0441\u0435 \u0434\u0430\u043D\u043D\u044B\u0435 \u043F\u0435\u0440\u0435\u0434 \u0441\u043E\u0437\u0434\u0430\u043D\u0438\u0435\u043C. \u0415\u0441\u043B\u0438 \u0447\u0442\u043E-\u0442\u043E \u043D\u0435\u043F\u0440\u0430\u0432\u0438\u043B\u044C\u043D\u043E \u2014 \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \u00AB\u041D\u0430\u0437\u0430\u0434\u00BB \u0434\u043B\u044F \u0438\u0441\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u044F. \u041A\u043B\u044E\u0447\u0438 \u0438 \u0441\u0435\u043A\u0440\u0435\u0442\u044B \u0437\u0430\u043C\u0430\u0441\u043A\u0438\u0440\u043E\u0432\u0430\u043D\u044B \u0434\u043B\u044F \u0431\u0435\u0437\u043E\u043F\u0430\u0441\u043D\u043E\u0441\u0442\u0438."),
            React.createElement("table", { className: classes.ReviewTable },
                React.createElement("tbody", null,
                    React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u041F\u0440\u043E\u0435\u043A\u0442"),
                        React.createElement("td", null, getProjectName())
                    ),
                    React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435"),
                        React.createElement("td", null, form.name)
                    ),
                    React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u0421\u0442\u0430\u0442\u0443\u0441"),
                        React.createElement("td", null,
                            React.createElement("span", {
                                className: classes.StatusBadge + " " + (form.status === 1 ? classes.StatusActive : classes.StatusInactive)
                            }, form.status === 1 ? "\u0410\u043A\u0442\u0438\u0432\u0435\u043D" : "\u041D\u0435\u0430\u043A\u0442\u0438\u0432\u0435\u043D")
                        )
                    ),
                    React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u0422\u0438\u043F \u043F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440\u0430"),
                        React.createElement("td", null, reviewProviderCfg ? reviewProviderCfg.label : "\u2014")
                    ),
                    React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u041F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440"),
                        React.createElement("td", null, form.providerName || "\u2014")
                    ),
                    authReviewRows,
                    React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u041C\u0435\u0442\u043E\u0434\u044B"),
                        React.createElement("td", null,
                            enabledMethods.length === 0
                                ? React.createElement("span", { className: classes.ReviewMuted }, "\u041D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D\u044B")
                                : enabledMethods.map(function (m) {
                                    return React.createElement("span", { key: m.key, className: classes.MethodBadge }, m.label);
                                })
                        )
                    ),
                    form.paymentMethod ? React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u0421\u043F\u043E\u0441\u043E\u0431 \u043E\u043F\u043B\u0430\u0442\u044B"),
                        React.createElement("td", null,
                            React.createElement("span", { className: classes.MethodBadge },
                                form.paymentMethod === "SBP_CROSS_BORDER" ? "\u0421\u0411\u041F" : "P2P"
                            )
                        )
                    ) : null,
                    React.createElement("tr", null,
                        React.createElement("td", { className: classes.ReviewLabel }, "\u041A\u043E\u043C\u0438\u0441\u0441\u0438\u0438"),
                        React.createElement("td", null,
                            (form.feePaymentCost || form.feePaymentFixed || form.feePayoutCost || form.feePayoutFixed || form.feeRefundCost || form.feeRefundFixed) ?
                                React.createElement("span", null,
                                    (form.feePaymentCost || form.feePaymentFixed) ? "Payment: " + (form.feePaymentCost || "0") + "% + " + (form.feePaymentFixed || "0") + "\u20BD. " : "",
                                    (form.feePayoutCost || form.feePayoutFixed) ? "Payout: " + (form.feePayoutCost || "0") + "% + " + (form.feePayoutFixed || "0") + "\u20BD. " : "",
                                    (form.feeRefundCost || form.feeRefundFixed) ? "Refund: " + (form.feeRefundCost || "0") + "% + " + (form.feeRefundFixed || "0") + "\u20BD" : ""
                                )
                                : React.createElement("span", { className: classes.ReviewMuted }, "\u041D\u0435 \u0443\u043A\u0430\u0437\u0430\u043D\u044B")
                        )
                    )
                )
            ),
            React.createElement("div", { className: classes.ReviewNote },
                "\u041F\u043E\u0441\u043B\u0435 \u0441\u043E\u0437\u0434\u0430\u043D\u0438\u044F \u0432\u044B \u0441\u043C\u043E\u0436\u0435\u0442\u0435 \u043D\u0430\u0441\u0442\u0440\u043E\u0438\u0442\u044C \u043A\u043E\u043C\u0438\u0441\u0441\u0438\u0438, \u043B\u0438\u043C\u0438\u0442\u044B \u0438 \u0438\u043D\u0442\u0435\u0433\u0440\u0430\u0446\u0438\u044E \u0432\u043E \u0432\u043A\u043B\u0430\u0434\u043A\u0430\u0445 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B\u0430."
            )
        );
    }

    // Navigation buttons
    var navButtons = React.createElement("div", { className: classes.NavButtons },
        step > 1
            ? React.createElement("button", {
                className: classes.BtnBack,
                onClick: goBack,
                disabled: creating,
            }, "\u2190 \u041D\u0430\u0437\u0430\u0434")
            : React.createElement("div", null),
        React.createElement("div", { className: classes.NavRight },
            step < 6 ? React.createElement("button", {
                className: classes.BtnNext,
                onClick: goNext,
                disabled: !canGoNext(),
            }, "\u0414\u0430\u043B\u0435\u0435 \u2192") : null,
            step === 6 ? React.createElement("button", {
                className: classes.BtnCreate,
                onClick: create,
                disabled: creating,
            }, creating ? "\u0421\u043E\u0437\u0434\u0430\u043D\u0438\u0435..." : "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B") : null
        )
    );

    return React.createElement("div", {
        className: classes.Overlay,
        onClick: function (e) { if (e.target === e.currentTarget && !creating) onClose(); },
    },
        React.createElement("div", { className: classes.WizardCard },
            React.createElement("div", { className: classes.WizardHeader },
                React.createElement("h2", { className: classes.WizardTitle }, "\u041D\u043E\u0432\u044B\u0439 \u0442\u0435\u0440\u043C\u0438\u043D\u0430\u043B"),
                React.createElement("button", {
                    className: classes.CloseBtn,
                    onClick: onClose,
                    disabled: creating,
                }, "\u2715")
            ),
            progressBar,
            stepContent,
            navButtons
        )
    );
}

export default TerminalAddWizard;



// WEBPACK FOOTER //
// ./src/Components/TerminalAddWizard/index.js